/**
 * ResponsiveModal Component
 * Desktop: Centered modal
 * Mobile: Full-screen modal or bottom sheet
 *
 * Features:
 * - Automatic responsive layout
 * - Smooth animations
 * - Backdrop click handling
 * - ESC key support
 * - Scroll management
 */

import { ReactNode, useEffect } from 'react';
import { X } from 'lucide-react';
import { useIsMobileOrTablet } from '../../hooks/useMediaQuery';
import { cn } from '../../utils/responsive';

export interface ResponsiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Modal size on desktop */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  /** Mobile display style */
  mobileStyle?: 'fullscreen' | 'bottom-sheet';
  /** Prevent closing on backdrop click */
  preventBackdropClose?: boolean;
  /** Prevent closing on ESC key */
  preventEscapeClose?: boolean;
  className?: string;
}

export function ResponsiveModal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  mobileStyle = 'fullscreen',
  preventBackdropClose = false,
  preventEscapeClose = false,
  className,
}: ResponsiveModalProps) {
  const isMobileOrTablet = useIsMobileOrTablet();

  // Handle ESC key
  useEffect(() => {
    if (!isOpen || preventEscapeClose) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, preventEscapeClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !preventBackdropClose) {
      onClose();
    }
  };

  // Desktop size classes
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-full mx-4',
  };

  // Mobile: Full-screen modal
  if (isMobileOrTablet && mobileStyle === 'fullscreen') {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-gray-900">
        {/* Mobile Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 dark:border-gray-700 bg-white dark:bg-gray-900 sticky top-0 z-10">
          <h2 className="text-lg font-semibold text-stone-900 dark:text-gray-100 truncate flex-1">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-gray-700 active:scale-95
                     transition-all touch-target ml-2"
            aria-label="ปิด"
          >
            <X className="w-5 h-5 text-stone-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Mobile Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4">{children}</div>
        </div>

        {/* Mobile Footer */}
        {footer && (
          <div className="border-t border-stone-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900 sticky bottom-0">
            {footer}
          </div>
        )}
      </div>
    );
  }

  // Mobile: Bottom Sheet
  if (isMobileOrTablet && mobileStyle === 'bottom-sheet') {
    return (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/40 dark:bg-black/70 backdrop-blur-sm z-40 animate-fade-in"
          onClick={handleBackdropClick}
        />

        {/* Bottom Sheet */}
        <div
          className="fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-gray-800 rounded-t-2xl shadow-2xl
                   max-h-[90vh] flex flex-col animate-in slide-in-from-bottom duration-300"
        >
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1.5 bg-stone-300 dark:bg-gray-600 rounded-full" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-4 pb-3 border-b border-stone-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-stone-900 dark:text-gray-100 truncate flex-1">
              {title}
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-gray-700 active:scale-95
                       transition-all touch-target ml-2"
              aria-label="ปิด"
            >
              <X className="w-5 h-5 text-stone-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4">{children}</div>
          </div>

          {/* Footer */}
          {footer && (
            <div className="border-t border-stone-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
              {footer}
            </div>
          )}
        </div>
      </>
    );
  }

  // Desktop: Centered Modal
  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 dark:bg-black/70 backdrop-blur-sm z-40 animate-fade-in"
        onClick={handleBackdropClick}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className={cn(
            'bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full pointer-events-auto',
            'max-h-[90vh] flex flex-col',
            'animate-in fade-in-up duration-200',
            sizeClasses[size],
            className
          )}
        >
          {/* Header */}
          {title && (
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-stone-900 dark:text-gray-100">
                {title}
              </h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-gray-700 active:scale-95
                         transition-all"
                aria-label="ปิด"
              >
                <X className="w-5 h-5 text-stone-600 dark:text-gray-400" />
              </button>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="border-t border-stone-200 dark:border-gray-700 px-6 py-4 bg-stone-50 dark:bg-gray-900">
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Modal Footer Helper Components
 */
interface ModalFooterProps {
  children: ReactNode;
  className?: string;
}

export function ModalFooter({ children, className }: ModalFooterProps) {
  return (
    <div className={cn('flex items-center justify-end gap-3', className)}>
      {children}
    </div>
  );
}

interface ModalButtonProps {
  onClick: () => void;
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
  className?: string;
}

export function ModalButton({
  onClick,
  children,
  variant = 'primary',
  disabled = false,
  className,
}: ModalButtonProps) {
  const variantClasses = {
    primary: 'btn-hyb-primary',
    secondary: 'btn-hyb-secondary',
    danger: 'bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2.5 rounded-lg shadow-sm active:scale-[0.98] transition-all duration-200',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        variantClasses[variant],
        disabled && 'opacity-50 cursor-not-allowed',
        'touch-target',
        className
      )}
    >
      {children}
    </button>
  );
}
